import React, { useEffect, useState } from "react";
import client, { apiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { usePaged, Paginator } from "@/components/Paginator";
import { Plus, Pencil, Trash2, Tag } from "lucide-react";

export default function Categories() {
  const { user } = useAuth();
  const canDelete = ["super_admin", "admin"].includes(user.role);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [editId, setEditId] = useState(null);

  const load = () => client.get("/categories").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const paged = usePaged(items, 10);

  const save = async () => {
    if (!name.trim()) { toast.error("Nama kategori wajib diisi"); return; }
    try {
      if (editId) await client.put(`/categories/${editId}`, { name });
      else await client.post("/categories", { name });
      toast.success("Kategori disimpan"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (c) => {
    if (!window.confirm(`Hapus kategori "${c.name}"?`)) return;
    try { await client.delete(`/categories/${c.id}`); toast.success("Kategori dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6 space-y-5 animate-in-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2"><Tag className="h-7 w-7 text-primary" /> Kategori</h1>
          <p className="text-sm text-muted-foreground">Kelola kategori produk</p>
        </div>
        <Button onClick={() => { setName(""); setEditId(null); setOpen(true); }} data-testid="add-category-btn"><Plus className="mr-2 h-4 w-4" /> Tambah Kategori</Button>
      </div>

      <Card className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Nama Kategori</TableHead><TableHead className="w-32 text-right">Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {paged.slice.map((c) => (
              <TableRow key={c.id} className="hover:bg-slate-50" data-testid={`category-row-${c.id}`}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => { setName(c.name); setEditId(c.id); setOpen(true); }} data-testid={`edit-category-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                  {canDelete && <Button size="icon" variant="ghost" className="text-rose-500 hover:text-rose-600" onClick={() => del(c)} data-testid={`del-category-${c.id}`}><Trash2 className="h-4 w-4" /></Button>}
                </TableCell>
              </TableRow>
            ))}
            {!items.length && <TableRow><TableCell colSpan={2} className="py-10 text-center text-muted-foreground">Belum ada kategori</TableCell></TableRow>}
          </TableBody>
        </Table>
        <Paginator {...paged} />
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>{editId ? "Ubah" : "Tambah"} Kategori</DialogTitle><DialogDescription>Masukkan nama kategori produk.</DialogDescription></DialogHeader>
          <div className="space-y-2"><Label>Nama</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="category-name" autoFocus /></div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} data-testid="save-category-btn">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
